import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Crown, X, Send, Briefcase, Users, Code, Megaphone, Sparkles, Palette } from 'lucide-react';
import Footer from '../components/footer';
import { Button } from '@/components/ui/Button';

interface Role {
  id: string;
  title: string;
  category: 'Discord' | 'Fiverflow';
  icon: React.ReactNode;
  description: string;
  requirements?: string[];
}

const roles: Role[] = [
  {
    id: 'discord-moderator',
    title: 'Moderator',
    category: 'Discord',
    icon: <Users className="w-6 h-6" />,
    description: 'Maintenir une communauté saine et accueillante sur notre serveur Discord.',
    requirements: [
      'Expérience en modération de communautés Discord',
      'Bonne communication et résolution de conflits',
      'Disponibilité régulière'
    ]
  },
  {
    id: 'discord-community-manager',
    title: 'Community Manager',
    category: 'Discord',
    icon: <Users className="w-6 h-6" />,
    description: 'Animer et développer notre communauté Discord, créer du contenu engageant.',
    requirements: [
      'Expérience en gestion de communauté',
      'Créativité et sens de l\'animation',
      'Bonne connaissance de Discord'
    ]
  },
  {
    id: 'fiverflow-developer',
    title: 'Developer',
    category: 'Fiverflow',
    icon: <Code className="w-6 h-6" />,
    description: 'Développer et améliorer les fonctionnalités de la plateforme Fiverflow.',
    requirements: [
      'Expérience en développement web (React, TypeScript)',
      'Connaissance de bases de données',
      'Esprit d\'équipe et communication'
    ]
  },
  {
    id: 'fiverflow-marketing',
    title: 'Marketing',
    category: 'Fiverflow',
    icon: <Megaphone className="w-6 h-6" />,
    description: 'Promouvoir Fiverflow et développer notre présence en ligne.',
    requirements: [
      'Expérience en marketing digital',
      'Création de contenu et stratégie',
      'Connaissance des réseaux sociaux'
    ]
  },
  {
    id: 'fiverflow-creator',
    title: 'Creator',
    category: 'Fiverflow',
    icon: <Sparkles className="w-6 h-6" />,
    description: 'Créer du contenu engageant pour promouvoir Fiverflow.',
    requirements: [
      'Expérience en création de contenu',
      'Portfolio de réalisations',
      'Créativité et originalité'
    ]
  },
  {
    id: 'fiverflow-designer',
    title: 'Designer',
    category: 'Fiverflow',
    icon: <Palette className="w-6 h-6" />,
    description: 'Créer des designs modernes et intuitifs pour l\'interface utilisateur.',
    requirements: [
      'Expérience en design UI/UX',
      'Maîtrise d\'outils de design',
      'Portfolio de projets'
    ]
  }
];

interface ApplicationFormData {
  roleId: string;
  name: string;
  email: string;
  discord: string;
  experience: string;
  motivation: string;
  portfolio?: string;
}

const WorkWithUsPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState<ApplicationFormData>({
    roleId: '',
    name: '',
    email: '',
    discord: '',
    experience: '',
    motivation: '',
    portfolio: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleRoleClick = (role: Role) => {
    setSelectedRole(role);
    setFormData({
      roleId: role.id,
      name: user?.user_metadata?.full_name || '',
      email: user?.email || '',
      discord: '',
      experience: '',
      motivation: '',
      portfolio: ''
    });
    setSubmitSuccess(false);
  };

  const handleCloseModal = () => {
    setSelectedRole(null);
    setFormData({
      roleId: '',
      name: '',
      email: '',
      discord: '',
      experience: '',
      motivation: '',
      portfolio: ''
    });
    setSubmitSuccess(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // TODO: Envoyer les données au webhook Discord
      // Pour l'instant, on simule juste l'envoi
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitSuccess(true);
      setTimeout(() => {
        handleCloseModal();
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const discordRoles = roles.filter(r => r.category === 'Discord');
  const fiverflowRoles = roles.filter(r => r.category === 'Fiverflow');

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm border-b border-[rgb(var(--border))] z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Crown className="text-white" size={20} />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                FiverFlow
              </h1>
            </Link>
            
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</Link>
              <Link to="/pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</Link>
              <span className="text-[rgb(var(--brand))] font-medium">Work with us</span>
            </nav>
            
            <div className="flex items-center space-x-4">
              {user ? (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-full hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Dashboard
                  <ArrowRight size={16} className="ml-2" />
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-full hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-100 text-[rgb(var(--brand))] text-xs font-semibold mb-5">
              <Briefcase className="w-4 h-4 mr-2" /> Join our team
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Work with us
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Rejoignez l'équipe Fiverflow et contribuez à révolutionner la gestion freelance. 
              Nous recherchons des talents passionnés pour développer notre communauté et notre plateforme.
            </p>
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Discord Roles */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Discord</h2>
            <p className="text-gray-600 mb-8">Rejoignez notre équipe de modération et de gestion de communauté</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {discordRoles.map((role) => (
                <div
                  key={role.id}
                  onClick={() => handleRoleClick(role)}
                  className="group cursor-pointer rounded-xl border-2 border-gray-200 hover:border-blue-500 bg-white p-6 transition-all duration-200 shadow-sm hover:shadow-lg"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-blue-100 rounded-lg text-blue-600">
                        {role.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{role.title}</h3>
                        <span className="text-sm text-gray-500">{role.category}</span>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <p className="text-gray-600 mb-4">{role.description}</p>
                  {role.requirements && role.requirements.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">Requis :</p>
                      <ul className="space-y-1">
                        {role.requirements.slice(0, 2).map((req, idx) => (
                          <li key={idx} className="text-sm text-gray-600 flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Fiverflow Roles */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Fiverflow</h2>
            <p className="text-gray-600 mb-8">Contribuez au développement et à la croissance de la plateforme</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {fiverflowRoles.map((role) => (
                <div
                  key={role.id}
                  onClick={() => handleRoleClick(role)}
                  className="group cursor-pointer rounded-xl border-2 border-gray-200 hover:border-purple-500 bg-white p-6 transition-all duration-200 shadow-sm hover:shadow-lg"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-purple-100 rounded-lg text-purple-600">
                        {role.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{role.title}</h3>
                        <span className="text-sm text-gray-500">{role.category}</span>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <p className="text-gray-600 mb-4 text-sm">{role.description}</p>
                  {role.requirements && role.requirements.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">Requis :</p>
                      <ul className="space-y-1">
                        {role.requirements.slice(0, 2).map((req, idx) => (
                          <li key={idx} className="text-sm text-gray-600 flex items-start">
                            <span className="text-purple-600 mr-2">•</span>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Modal */}
      {selectedRole && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Candidature - {selectedRole.title}</h2>
                <p className="text-sm text-gray-500">{selectedRole.category}</p>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Candidature envoyée !</h3>
                  <p className="text-gray-600">Nous vous contacterons bientôt.</p>
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="discord" className="block text-sm font-medium text-gray-700 mb-2">
                      Discord Username *
                    </label>
                    <input
                      type="text"
                      id="discord"
                      name="discord"
                      value={formData.discord}
                      onChange={handleInputChange}
                      required
                      placeholder="ex: username#1234"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                      Expérience *
                    </label>
                    <textarea
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      placeholder="Décrivez votre expérience pertinente pour ce rôle..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="motivation" className="block text-sm font-medium text-gray-700 mb-2">
                      Motivation *
                    </label>
                    <textarea
                      id="motivation"
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      placeholder="Pourquoi souhaitez-vous rejoindre l'équipe Fiverflow ?"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  {(selectedRole.id === 'fiverflow-creator' || selectedRole.id === 'fiverflow-designer') && (
                    <div>
                      <label htmlFor="portfolio" className="block text-sm font-medium text-gray-700 mb-2">
                        Portfolio / Liens (optionnel)
                      </label>
                      <input
                        type="url"
                        id="portfolio"
                        name="portfolio"
                        value={formData.portfolio}
                        onChange={handleInputChange}
                        placeholder="https://..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Annuler
                    </button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-2.5"
                    >
                      {isSubmitting ? 'Envoi...' : 'Envoyer la candidature'}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default WorkWithUsPage;

